import { useBranding } from "@app-setup:BrandingContext";
import { Box, SxProps } from "@mui/material";
import { useTranslation } from "react-i18next";

import { SVGInternalIcon } from "./SVGInternalIcon";

// Default logo dimensions - centralized for consistency
// Nemo logo SVG has viewBox 543.22x113.2 (aspect ratio ~4.8:1)
// Width 120px → height ~25px to maintain aspect ratio
const DEFAULT_LOGO_WIDTH = 120;
const DEFAULT_LOGO_HEIGHT = 25;

interface BrandedLogoProps {
  sx?: SxProps;
  // Fallback to default logo when no branding
  fallbackIcon?: "nemoIcon" | "innovumIcon";
}

/**
 * Displays organization's custom logo if branding is configured,
 * otherwise falls back to the default Nemo logo.
 * Uses default dimensions (120x25) unless overridden via sx prop.
 */
export const BrandedLogo = ({
  sx,
  fallbackIcon = "nemoIcon",
}: BrandedLogoProps) => {
  const { t } = useTranslation();
  const { config } = useBranding();

  // For custom logos (base64 images), use maxWidth/maxHeight for flexibility
  const imageSx: SxProps = {
    maxWidth: DEFAULT_LOGO_WIDTH,
    maxHeight: DEFAULT_LOGO_HEIGHT * 1.6, // Allow taller custom logos (40px)
  };

  // For SVG icons, use explicit width/height to avoid SvgIcon square constraint
  const svgSx: SxProps = {
    width: DEFAULT_LOGO_WIDTH,
    height: DEFAULT_LOGO_HEIGHT,
  };

  if (config?.logoBase64) {
    return (
      <Box
        component="img"
        src={config.logoBase64}
        alt={t("components.logoAlt")}
        sx={{
          objectFit: "contain",
          ...imageSx,
          ...sx,
        }}
      />
    );
  }

  return <SVGInternalIcon icon={fallbackIcon} sx={{ ...svgSx, ...sx }} />;
};
